import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Textarea,
  makeStyles,
} from '@fluentui/react-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus } from 'lucide-react';

import { ApiError } from '../api/client';
import { MigrationGroup, createMigrationGroup } from '../api/migrationGroups';
import { ACCENT } from '../theme/tokens';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '24px',
    maxWidth: '560px',
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: '6px',
    // FD-466 — the accent TEXT rung, from the token module rather than a
    // literal. Every one of these back links carried a hardcoded hex through
    // two accent retirements (sky-700, then Rubix orange) and the drift gate
    // caught them both times; taking the value from `ACCENT` is what stops a
    // third.
    color: ACCENT.pressed,
    fontSize: '13px',
    textDecorationLine: 'none',
    ':hover': {
      textDecorationLine: 'underline',
    },
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '16px',
    backgroundColor: '#ffffff',
    padding: '24px',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: '#e4e4e7',
    borderRightColor: '#e4e4e7',
    borderBottomColor: '#e4e4e7',
    borderLeftColor: '#e4e4e7',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
  },
  actions: {
    display: 'flex',
    columnGap: '8px',
    justifyContent: 'flex-end',
  },
  hint: {
    fontSize: '13px',
    color: '#71717a', // zinc-500
  },
});

// Surfaces the server's 409 GROUP_NAME_CONFLICT as an actionable inline error
// rather than a generic failure. The server returns the EXISTING group's id
// alongside the conflict, so the operator can open it instead of guessing which
// of their groups already holds the name.
function conflictGroupId(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const body = error.body as { data?: { migrationGroupId?: string } } | undefined;
  return body?.data?.migrationGroupId ?? null;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const body = error.body as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return error instanceof Error ? error.message : 'Unexpected error';
}

export default function CreateGroupPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const mutation = useMutation<MigrationGroup, unknown, void>({
    mutationFn: () =>
      createMigrationGroup({
        name: name.trim(),
        description: description.trim() || undefined,
      }),
    onSuccess: (group) => {
      queryClient.invalidateQueries({ queryKey: ['migration-groups'] });
      navigate(`/groups/${encodeURIComponent(group.migrationGroupId)}/overview`);
    },
  });

  const existingId = conflictGroupId(mutation.error);

  return (
    <div className={styles.page}>
      <Link to="/groups" className={styles.backLink}>
        <ArrowLeft size={14} strokeWidth={1.5} />
        Back to groups
      </Link>

      <div>
        <h1 className="font-display text-3xl tracking-tight">New group</h1>
        <p className={styles.hint}>
          A group is a container for related migrations. It carries no settings and changes nothing
          about how a migration runs — it is for organization and reporting.
        </p>
      </div>

      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <Field label="Group name" required>
          <Input
            value={name}
            onChange={(_, d) => setName(d.value)}
            placeholder="Contoso"
            maxLength={128}
          />
        </Field>

        <Field
          label="Description"
          hint="Optional. What this group is for — a customer, a region, a phase of work."
        >
          <Textarea
            value={description}
            onChange={(_, d) => setDescription(d.value)}
            resize="vertical"
            maxLength={512}
          />
        </Field>

        {mutation.isError && (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Could not create the group</MessageBarTitle>
              {errorMessage(mutation.error)}
              {existingId && (
                <>
                  {' '}
                  <Link to={`/groups/${encodeURIComponent(existingId)}/overview`}>Open the existing group</Link>
                </>
              )}
            </MessageBarBody>
          </MessageBar>
        )}

        <div className={styles.actions}>
          <Button appearance="secondary" onClick={() => navigate('/groups')}>
            Cancel
          </Button>
          <Button
            appearance="primary"
            type="submit"
            icon={<Plus size={16} strokeWidth={1.5} />}
            disabled={!name.trim() || mutation.isPending}
          >
            {mutation.isPending ? 'Creating…' : 'Create group'}
          </Button>
        </div>
      </form>
    </div>
  );
}
